import { type ChangeEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { debounce } from "radash"
import PageLoader from "components/PageLoader"
import { type UICustomizationData } from "pages/UICustomization/types.ts"
import { getFontsTemplate } from "./fontsTemplate"
import { useFonts } from "hooks/query/useFontsQuery"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface UICustomizationFontsProps {
  data: UICustomizationData
  onChange: (data: UICustomizationData) => void
}

export const UICustomizationFonts = ({ data, onChange }: UICustomizationFontsProps) => {
  const { t } = useTranslation()
  const { data: fontsData, isLoading: loading, isError } = useFonts()
  const fontsList = Object.keys(fontsData ?? {})

  const translatedFontsTemplate = getFontsTemplate(t)

  const [inputValues, setInputValues] = useState<Record<string, string>>(() => {
    const initialValues: Record<string, string> = {}
    translatedFontsTemplate.forEach(({ keyFamily }) => {
      initialValues[keyFamily] = data.styles[keyFamily] || ""
    })
    return initialValues
  })

  const [filteredFonts, setFilteredFonts] = useState<Record<string, string[]>>({})

  const debouncedOnChange = debounce({ delay: 100 }, (newData: UICustomizationData) => {
    onChange(newData)
  })

  const handleInput = (keyFamily: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValues((prev) => ({ ...prev, [keyFamily]: value }))

    // Filter fonts list based on input
    if (value && value.length > 0) {
      const filtered = fontsList
        .filter((name) => name.toLowerCase().includes(value.toLowerCase()))
        .slice(0, 7)
      setFilteredFonts((prev) => ({ ...prev, [keyFamily]: filtered }))
    } else {
      setFilteredFonts((prev) => ({ ...prev, [keyFamily]: fontsList.slice(0, 7) }))
    }

    debouncedOnChange({
      ...data,
      styles: {
        ...data.styles,
        [keyFamily]: value,
      },
    })
  }

  const handleFontSelect = (keyFamily: string, fontName: string) => {
    setInputValues((prev) => ({ ...prev, [keyFamily]: fontName }))
    setFilteredFonts((prev) => ({ ...prev, [keyFamily]: [] }))
    debouncedOnChange({
      ...data,
      styles: {
        ...data.styles,
        [keyFamily]: fontName,
      },
    })
  }

  if (isError) return <p className="text-lg font-semibold text-destructive">{t("fetchError")}</p>

  return (
    <>
      {loading ? (
        <PageLoader />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {translatedFontsTemplate.map(({ title, subTitle, keyFamily, keyStyle, keyWeight }) => {
            const selectedFamily = data.styles[keyFamily]
            const selectedStyle = data.styles[keyStyle]
            const selectedWeight = data.styles[keyWeight]
            const familyStyles = selectedFamily ? fontsData?.[selectedFamily] : undefined
            const availableStyles = Object.keys(familyStyles ?? {})
            const availableWeights = familyStyles?.[selectedStyle] ?? []
            const suggestions = filteredFonts[keyFamily] || []

            return (
              <Card key={title}>
                <CardHeader>
                  <CardTitle>{title}</CardTitle>
                  <p className="text-xs text-muted-foreground">{subTitle}</p>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <div className="relative">
                    <Label htmlFor={keyFamily}>{t("uICustomizationFonts.fontFamily")}</Label>
                    <Input
                      id={keyFamily}
                      value={inputValues[keyFamily]}
                      className={!inputValues[keyFamily] ? "border-destructive" : ""}
                      onChange={handleInput(keyFamily)}
                      onFocus={() => {
                        const value = inputValues[keyFamily]
                        if (value && value.length > 0) {
                          const filtered = fontsList
                            .filter((name) => name.toLowerCase().includes(value.toLowerCase()))
                            .slice(0, 7)
                          setFilteredFonts((prev) => ({ ...prev, [keyFamily]: filtered }))
                        } else {
                          setFilteredFonts((prev) => ({
                            ...prev,
                            [keyFamily]: fontsList.slice(0, 7),
                          }))
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click on suggestion
                        setTimeout(() => {
                          setFilteredFonts((prev) => ({ ...prev, [keyFamily]: [] }))
                        }, 200)
                      }}
                    />
                    {suggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto">
                        {suggestions.map((fontName) => (
                          <div
                            key={fontName}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-accent"
                            onMouseDown={() => handleFontSelect(keyFamily, fontName)}
                          >
                            {fontName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedFamily && familyStyles && (
                    <div>
                      <Label>{t("uICustomizationFonts.fontStyle")}</Label>
                      <Select
                        value={selectedStyle}
                        onValueChange={(item: string) =>
                          debouncedOnChange({
                            ...data,
                            styles: {
                              ...data.styles,
                              [keyStyle]: item,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableStyles.map((style: string) => (
                            <SelectItem value={style} key={style}>
                              {style}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selectedFamily && familyStyles && selectedWeight && (
                    <div>
                      <Label>{t("uICustomizationFonts.fontWeight")}</Label>
                      <Select
                        value={selectedWeight}
                        onValueChange={(item: string) =>
                          debouncedOnChange({
                            ...data,
                            styles: {
                              ...data.styles,
                              [keyWeight]: item,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableWeights.map((style: string) => (
                            <SelectItem value={style} key={style}>
                              {style}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </>
  )
}
